import React, { useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	List,
	ListItem,
	ListItemAvatar,
	Avatar,
	ListItemText,
	Box,
	Typography,
	IconButton,
	Button,
	Divider,
	CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { useAuth } from "@/hooks/auth";
import { Conversation } from "@/types/chat";
import ControllableConfirmation from "@/components/confirmation/controllable.confirmation";

interface ManageGroupDialogProps {
	open: boolean;
	onClose: () => void;
	conversation: Conversation;
	onRemoveMember: (userId: string) => Promise<void>;
	onDeleteGroup: () => Promise<void>;
}

export const ManageGroupDialog: React.FC<ManageGroupDialogProps> = ({
	open,
	onClose,
	conversation,
	onRemoveMember,
	onDeleteGroup,
}) => {
	const theme = useTheme();
	const { user: currentUser } = useAuth();
	
	const myRole = conversation.participants.find(p => p.id === currentUser?.id)?.role;
	const isOwner = myRole === "ADMIN";

	const [removingId, setRemovingId] = useState<string | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [confirmConfig, setConfirmConfig] = useState({
		title: "",
		content: "",
		severity: "error" as "error" | "warning",
		confirmLabel: "Батлах",
		onConfirm: async () => {},
	});

	const handleRemove = async (userId: string) => {
		setRemovingId(userId);
		try {
			await onRemoveMember(userId);
		} catch (err) {
			console.error("Failed to remove member", err);
		} finally {
			setRemovingId(null);
		}
	};

	const handleDelete = () => {
		if (isOwner) {
			setConfirmConfig({
				title: "Групп устгах",
				content: "Энэ группыг устгахдаа итгэлтэй байна уу? Устгасан тохиолдолд сэргээх боломжгүй.",
				severity: "error",
				confirmLabel: "Устгах",
				onConfirm: async () => {
					setIsDeleting(true);
					try {
						await onDeleteGroup();
						onClose();
					} catch (err) {
						console.error("Failed to delete group", err);
					} finally {
						setIsDeleting(false);
						setConfirmOpen(false);
					}
				}
			});
			setConfirmOpen(true);
		} else {
			setConfirmConfig({
				title: "Группээс гарах",
				content: "Группээс гарахдаа итгэлтэй байна уу?",
				severity: "warning",
				confirmLabel: "Гарах",
				onConfirm: async () => {
					setIsDeleting(true);
					try {
						if (currentUser) {
							await onRemoveMember(currentUser.id);
						}
						onClose();
					} catch (err) {
						console.error("Failed to leave group", err);
					} finally {
						setIsDeleting(false);
						setConfirmOpen(false);
					}
				}
			});
			setConfirmOpen(true);
		}
	};

	return (
		<Dialog 
			open={open} 
			onClose={onClose} 
			fullWidth 
			maxWidth="xs"
			PaperProps={{
				sx: {
					borderRadius: 3,
					backgroundImage: "none",
					backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.85)' : 'rgba(255, 255, 255, 0.95)',
					backdropFilter: "blur(16px)",
					boxShadow: theme.shadows[16],
				}
			}}
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Typography variant="h6" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<SettingsIcon color="primary" />
					Групп тохиргоо
				</Typography>
				<IconButton size="small" onClick={onClose} sx={{ color: "text.secondary", backgroundColor: "action.hover" }}>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			
			<Box sx={{ px: 3, py: 2, textAlign: "center" }}>
				<Typography variant="h6">{conversation.name || "Group Chat"}</Typography>
				<Typography variant="body2" color="text.secondary">
					Нийт {conversation.participants?.length || 0} гишүүнтэй
				</Typography>
			</Box>
			<Divider />

			<DialogContent
				sx={{ p: 0, display: "flex", flexDirection: "column", height: 350 }}
			>
				<Typography variant="caption" sx={{ px: 2, pt: 2, pb: 1, color: "text.secondary", fontWeight: "bold" }}>
					ГИШҮҮДИЙН ЖАГСААЛТ
				</Typography>

				<List 
					sx={{ 
						flexGrow: 1, 
						overflowY: "auto",
						"&::-webkit-scrollbar": { width: 6 },
						"&::-webkit-scrollbar-thumb": {
							backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
							borderRadius: 3,
						},
						px: 1,
					}}
				>
					{conversation.participants?.map((p) => {
						const isMe = p.id === currentUser?.id;
						return (
							<ListItem 
								key={p.id} 
								disablePadding 
								sx={{ mb: 0.5, px: 1, py: 0.5, borderRadius: 2, "&:hover": { backgroundColor: "action.hover" } }}
								secondaryAction={
									!isMe && isOwner && (
										<IconButton 
											edge="end" 
											aria-label="remove" 
											color="error" 
											size="small"
											onClick={() => handleRemove(p.id)}
											disabled={removingId === p.id || isDeleting}
										>
											{removingId === p.id ? <CircularProgress size={16} /> : <PersonRemoveIcon fontSize="small" />}
										</IconButton>
									)
								}
							>
								<ListItemAvatar>
									<Avatar src={p.avatar} alt={p.firstName} sx={{ width: 40, height: 40 }}>
										{p.firstName?.[0] || p.username?.[0] || "?"}
									</Avatar>
								</ListItemAvatar>
								<ListItemText
									primary={
										<Typography variant="subtitle2" color="text.primary" fontWeight="600" noWrap>
											{[p.firstName, p.lastName]
												.filter((n) => n && n !== "undefined")
												.join(" ") ||
											(p.username !== "undefined" ? p.username : "") ||
											"Unknown User"}
											{isMe && " (Та)"}
										</Typography>
									}
									secondary={
										<Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
											<Box
												sx={{
													width: 8,
													height: 8,
													borderRadius: "50%",
													backgroundColor: p.isOnline ? "success.main" : "text.disabled",
												}}
											/>
											<Typography variant="caption" color="text.secondary">
												{p.isOnline ? "Онлайн" : "Офлайн"}
											</Typography>
										</Box>
									}
								/>
							</ListItem>
						);
					})}
				</List>
			</DialogContent>
			
			<Divider />
			
			<DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
				<Button 
					color="error" 
					startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : (isOwner ? <DeleteOutlineIcon /> : <PersonRemoveIcon />)}
					onClick={handleDelete}
					disabled={isDeleting || removingId !== null}
				>
					{isOwner ? "Групп устгах" : "Группээс гарах"}
				</Button>
				<Button onClick={onClose} color="inherit" disabled={isDeleting || removingId !== null}>
					Хаах
				</Button>
			</DialogActions>

			<ControllableConfirmation
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				title={confirmConfig.title}
				content={confirmConfig.content}
				severity={confirmConfig.severity}
				confirmLabel={confirmConfig.confirmLabel}
				onConfirm={confirmConfig.onConfirm}
				loading={isDeleting}
			/>
		</Dialog>
	);
};
